import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import DeliveryPicker from "@/components/orders/DeliveryPicker";
import { meApi, type DeliveryInput } from "@/lib/api";
import { STATUS_META, ORDER_STATUS_FLOW, money, fmtDate } from "@/lib/order";

export default function OrderDetailPage() {
  const { id = "" } = useParams();
  const [data, setData] = useState<{ order: any; items: any[]; payments: any[] } | null>(null);
  const [editing, setEditing] = useState(false);
  const [delivery, setDelivery] = useState<DeliveryInput>({});
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await meApi.order(id);
    setData(r);
    setDelivery({
      method: r.order.delivery_method || "courier",
      phone: r.order.delivery_phone || "",
      address: r.order.delivery_address || "",
      locker_code: r.order.locker_code || "",
      locker_name: r.order.locker_name || "",
    });
    setNote(r.order.customer_note || "");
  }
  useEffect(() => {
    load().catch(() => setData(null));
  }, [id]);

  if (!data) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-[#0b111c] text-white">
          <div className="mx-auto max-w-3xl px-6 py-12 text-gray-400">Loading order…</div>
        </main>
      </>
    );
  }

  const { order, items, payments } = data;
  const locked = !!order.locked || order.payment_status === "paid";
  const meta = STATUS_META[order.status] ?? STATUS_META.draft;
  const box = "rounded-2xl bg-[#11192b] border border-sky-500/10 p-6";

  async function accept() {
    setBusy(true);
    setMsg("");
    try {
      await meApi.acceptOrder(id);
      await load();
      setMsg("Quote accepted. We'll confirm payment and start production.");
    } catch (e: any) {
      setMsg(e.message || "Could not accept");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdits() {
    setBusy(true);
    setMsg("");
    try {
      await meApi.updateOrder(id, { customer_note: note, delivery });
      await load();
      setEditing(false);
    } catch (e: any) {
      setMsg(e.message || "Could not save");
    } finally {
      setBusy(false);
    }
  }

  const activeIdx = ORDER_STATUS_FLOW.indexOf(order.status);

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[#0b111c] text-white">
        <div className="mx-auto max-w-3xl px-6 py-12">
          <Link to="/account" className="text-sky-400 text-sm hover:underline">
            ← My account
          </Link>
          <div className="mt-2 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-black">Order {order.order_number}</h1>
              <p className="text-gray-400 text-sm mt-1">Placed {fmtDate(order.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              {order.payment_status === "paid" && (
                <span className="rounded-full bg-emerald-500/20 text-emerald-300 px-4 py-1.5 text-sm font-bold">
                  ✓ PAID
                </span>
              )}
              <span className={`rounded-full px-4 py-1.5 text-sm font-semibold ${meta.className}`}>
                {meta.label}
              </span>
            </div>
          </div>

          {/* Progress */}
          {order.status !== "cancelled" && (
            <div className="mt-8 flex items-center">
              {ORDER_STATUS_FLOW.map((s, i) => (
                <div key={s} className="flex items-center flex-1 last:flex-none">
                  <div
                    className={`h-3 w-3 rounded-full ${
                      i <= activeIdx ? "bg-sky-400" : "bg-slate-700"
                    }`}
                    title={STATUS_META[s].label}
                  />
                  {i < ORDER_STATUS_FLOW.length - 1 && (
                    <div className={`h-0.5 flex-1 ${i < activeIdx ? "bg-sky-400" : "bg-slate-700"}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          {msg && <p className="mt-4 text-sky-300">{msg}</p>}

          {/* Quote + accept */}
          <div className={`${box} mt-8`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Quote total</p>
                <p className="text-2xl font-black">
                  {order.quote_amount != null ? money(order.quote_amount) : "Awaiting quote"}
                </p>
              </div>
              {order.status === "quoted" && (
                <button
                  onClick={accept}
                  disabled={busy}
                  className="rounded-xl bg-emerald-500 px-6 py-3 font-bold text-white hover:bg-emerald-400 disabled:opacity-60"
                >
                  Accept quote
                </button>
              )}
            </div>
            {order.status === "quoted" && (
              <p className="mt-3 text-xs text-amber-300/90">
                Please check your design carefully. Once you accept and pay, the order is final — you
                are responsible for any spelling or layout errors you approved.
              </p>
            )}
          </div>

          {/* Items */}
          <div className={`${box} mt-6`}>
            <h2 className="font-bold text-lg mb-4">Items</h2>
            <div className="space-y-3">
              {items.map((it) => (
                <div key={it.id} className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-lg bg-[#0b111c] overflow-hidden flex items-center justify-center shrink-0">
                    {it.thumbnail ? (
                      <img src={it.thumbnail} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <span className="text-2xl">🎨</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{it.description}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                      {it.kind === "gangsheet" ? "Gang Sheet" : "T-Shirt"} · Qty {it.qty}
                      {it.garment_color && (
                        <>
                          ·
                          <span className="inline-block h-3 w-3 rounded-full border border-white/30" style={{ backgroundColor: it.garment_color }} />
                        </>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    {it.unit_price ? <p className="text-sm text-gray-300">{money(it.unit_price * it.qty)}</p> : null}
                    {it.design_id && !locked && (
                      <Link to={`/studio?design=${it.design_id}`} className="text-sky-400 text-xs hover:underline">
                        Edit in studio
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery */}
          <div className={`${box} mt-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg">Delivery</h2>
              {!locked &&
                (editing ? (
                  <div className="flex gap-3">
                    <button onClick={() => setEditing(false)} className="text-gray-400 text-sm hover:text-white">
                      Cancel
                    </button>
                    <button
                      onClick={saveEdits}
                      disabled={busy}
                      className="text-sky-300 text-sm font-semibold hover:underline"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setEditing(true)} className="text-sky-300 text-sm hover:underline">
                    Edit
                  </button>
                ))}
            </div>
            {editing ? (
              <DeliveryPicker value={delivery} onChange={setDelivery} />
            ) : order.delivery_method === "pudo" ? (
              <p className="text-gray-300">
                📦 Pudo Locker — <b>{order.locker_name}</b> ({order.locker_code})<br />
                <span className="text-sm text-gray-500">Cell: {order.delivery_phone || "—"}</span>
              </p>
            ) : order.delivery_method === "courier" ? (
              <p className="text-gray-300 whitespace-pre-wrap">
                🚚 Courier Guy<br />
                {order.delivery_address || "—"}
                <br />
                <span className="text-sm text-gray-500">Tel: {order.delivery_phone || "—"}</span>
              </p>
            ) : (
              <p className="text-gray-500">No delivery method chosen yet.</p>
            )}
          </div>

          {/* Notes */}
          <div className={`${box} mt-6`}>
            <h2 className="font-bold text-lg mb-2">Your notes</h2>
            {editing ? (
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-lg bg-[#0b111c] border border-sky-500/20 px-4 py-3 text-white"
              />
            ) : (
              <p className="text-gray-300 whitespace-pre-wrap">{order.customer_note || "—"}</p>
            )}
          </div>

          {/* Payments */}
          {payments.length > 0 && (
            <div className={`${box} mt-6`}>
              <h2 className="font-bold text-lg mb-4">Payments</h2>
              {payments.map((p, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span className="text-gray-400">
                    {fmtDate(p.created_at)} · {p.method}
                  </span>
                  <span className="text-emerald-300 font-semibold">{money(p.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {locked && (
            <p className="mt-6 text-xs text-gray-500">
              This order is paid and locked. Please contact us for any changes.
            </p>
          )}
        </div>
      </main>
    </>
  );
}
